package nl.volleyplanner.web.controller.thuisprogramma.assignee_finder

import kotlinx.coroutines.flow.*
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.entities.FullPerson
import nl.volleyplanner.api.entities.PersonId
import nl.volleyplanner.api.planner.ConstraintScoreImpact
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.api.planner.Ruleset
import nl.volleyplanner.api.planner.Task
import nl.volleyplanner.api.planner.TaskAssignmentAdvice
import nl.volleyplanner.api.sports.Match
import nl.volleyplanner.api.types.BigNumber
import nl.volleyplanner.extensions.now
import nl.volleyplanner.sportconfig.NevoboTeamName
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.*
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.textconverters.TaskLabelConverter
import nl.volleyplanner.web.component.scoreBadge
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaDataManager
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement

class AssigneeFinderStore {
    private val _state = MutableStateFlow<List<TaskAssignmentAdvice>?>(null)
    val state: StateFlow<List<TaskAssignmentAdvice>?> get() = _state
    private val _rulesetState = MutableStateFlow<Ruleset?>(null)
    val rulesetState: StateFlow<Ruleset?> get() = _rulesetState

    fun clearData() {
        _state.value = null
        _rulesetState.value = null
    }

    fun updateData(taskAssignmentAdvice: List<TaskAssignmentAdvice>?) {
        _state.value = taskAssignmentAdvice
    }

    fun updateRulesetData(ruleset: Ruleset) {
        _rulesetState.value = ruleset
    }
}

class TaskAssigneeStore(
    match: Match, task: Task
) {
    companion object {
        fun getKey(match: Match, task: Task) = "${match.id}-${task.id}"
    }
    private val _state = MutableStateFlow(task)
    val state: StateFlow<Task> get() = _state
    val task: Task get() = _state.value

    fun updateTask(task: Task) {
        _state.value = task
    }
}

class AssigneeFinder2(
    private val problem: PlanningProblem,
    private val problemVersionSlug: String?,
    private val match: Match,
    private val taskAssigneeStore: TaskAssigneeStore,
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager
) : OffcanvasComponent(
    elementId = "assigneefinder-${taskAssigneeStore.task.id}",
    cssClasses = setOf("offcanvas-assigneefinder"),
    offcanvasPosition = OffcanvasPosition.End,
    allowBodyScroll = true,
    showBackdrop = false
) {
    private val logger = Logger.getLogger("nl.volleyplanner.component.AssigneeFinder")

    private val assigneeFinderStore = AssigneeFinderStore()
    private val filterManager = FilterManager(assigneeFinderStore.state)
    private val assigneeFinderFilters = AssigneeFinderFilters(match, filterManager)

    private val updateAssigneeApiCallState = MutableStateFlow(false)

    init {
    }

    fun refreshData() {
        assigneeFinderStore.clearData()
        launchAsync(CoroutineManager.RENDER_SCOPE) {
            VolleyPlannerApplication.volleyplannerApiClient
                .organisation
                .plannerConfiguration
                .getTasksEndpoint(problem.slug, problemVersionSlug)
                .getTaskConstraints(taskAssigneeStore.task)
        }.onComplete { ruleset ->
            assigneeFinderStore.updateRulesetData(ruleset)
        }
        launchAsync(CoroutineManager.RENDER_SCOPE) {
            VolleyPlannerApplication.volleyplannerApiPlannerClient
                .getTaskAssignmentAdvice(problem.slug, problemVersionSlug, taskAssigneeStore.task)
        }.onComplete { taskAssignmentAdviceList ->
            assigneeFinderStore.updateData(taskAssignmentAdviceList)
        }
    }

    override fun onReady() {
        super.onReady()
        refreshData()
    }

    override fun onDispose() {
        super.onDispose()
        filterManager.dispose()
    }

    override fun FlowContent.renderHeader() {
        val task = taskAssigneeStore.task
        h5 {
            +"${task.startDateTime?.format(DATE_TIME_FORMAT)}: ${match.teamHome} - ${match.teamGuest}"
        }
        button(classes = "btn btn-close") {
            attributes["data-bs-dismiss"] = "offcanvas"
            attributes["aria-label"] = "Close"
        }
    }

    enum class RuleImpact(val cssClass: String) { Hard("danger"), Medium("warning"), Soft("secondary") }

    data class AdviceRule(
        val ruleName: String,
        val ruleImpact: RuleImpact,
        val humanReadableRuleName: String,
        val humanReadableImpact: String?,
        val ruleScore: BigNumber
    ) {
        constructor(constraintScoreImpact: ConstraintScoreImpact) : this(
            constraintScoreImpact.constraint.constraintName.name,
            when {
                constraintScoreImpact.impact.hardScore < 0 -> RuleImpact.Hard
                constraintScoreImpact.impact.mediumScore < 0 || constraintScoreImpact.impact.softScore < -10 -> RuleImpact.Medium
                else -> RuleImpact.Soft
            },
            TaskLabelConverter.toHumanReadable(constraintScoreImpact.constraint.constraintName),
            constraintScoreImpact.humanReadableImpact,
            minOf(constraintScoreImpact.impact.hardScore, constraintScoreImpact.impact.mediumScore, constraintScoreImpact.impact.softScore)
        )

        fun getUIString() : String {
            return if (humanReadableImpact == null) {
                "$humanReadableRuleName ($ruleScore)"
            } else {
                "$humanReadableRuleName ($ruleScore: $humanReadableImpact)"
            }
        }
    }

    /**
     * Utility function to convert the API advice into a usable list of rules for display purposes
     */
    fun List<ConstraintScoreImpact>.getAdviceRules(): List<AdviceRule> {
        val rulesList = map { AdviceRule(it) }
        return rulesList.sortedWith(compareBy<AdviceRule> { it.ruleImpact }.thenBy { it.ruleScore }
            .thenBy { it.ruleName })
    }

    inner class PersonTaskBadges(private val advice: TaskAssignmentAdvice?) : HTMLComponent() {
        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            if (advice == null) return consumer.div {}
            return consumer.div(classes = "") {
                div(classes = "d-flex border border-secondary rounded p-1 mb-1") {
                    strong { +"Van: " }
                    scoreBadge(advice.beforeScoreImpact)
                }

                div(classes = "vp-arrow-line") {
                    style = ""
                    div(classes = "ps-2 mb-1") {
                        advice.newScoreImpactList.getAdviceRules().forEach { adviceRule ->
                            span(classes = "badge me-1 text-bg-${adviceRule.ruleImpact.cssClass}") { +adviceRule.getUIString() }
                        }
                    }
                }

                div(classes = "d-flex border border-secondary rounded p-1") {
                    strong { +"Naar: " }
                    scoreBadge(advice.afterScoreImpact)
                }
            }
        }

        override suspend fun getState(): StateFlow<*>? = null
    }

    /**
     * Component that shows the currently assigned assignee together with the rule scorings
     */
    inner class CurrentAssigneeSelectionContainer : HTMLComponent() {
        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            val assignee = taskAssigneeStore.task.assignee
            return consumer.div(classes = "d-flex text-bg-light border border-secondary align-items-center p-2 mb-1") {
                if (updateAssigneeApiCallState.value) {
                    spinner()
                } else {
                    div {
                        strong { +"Huidige selectie: " }
                        span {
                            +"${assignee?.personAssignee?.name ?: "-"} ${assignee?.personAssignee?.groups?.get(GroupType.Team.paramValue)?.joinToString(", ") { NevoboTeamName.parse(it.name).teamIdentifier } ?: ""}"
                        }
                        if (assignee?.personAssignee != null) {
                            button(classes = "btn btn-sm ms-1") {
                                classes += "btn-secondary"
                                style =
                                    "--bs-btn-padding-y: .1rem; --bs-btn-padding-x: .2rem; --bs-btn-font-size: .75rem; margin-right: 0.1em;"

                                onClickFunction = { event ->
                                    selectAssignee(null)
                                }

                                i(classes = "bi bi-trash") {}
                                span(classes = "visually-hidden") {}
                            }
                        }
                    }
                    div {
                        if (assigneeFinderStore.state.value == null) {
                            spinner(small = true)
                        } else {
                            assigneeFinderStore.state.value?.find { it.person.id == assignee?.personAssignee?.id }?.let {
                                scoreBadge(it.afterScoreImpact)
                            }
                        }
                    }
                }
            }
        }

        override suspend fun getState(): StateFlow<*> {
            return taskAssigneeStore.state
                .combine(updateAssigneeApiCallState, { a, b -> Pair(a, b) })
                .stateIn(
                    CoroutineManager.RENDER_SCOPE,
                    SharingStarted.Eagerly,
                    Pair(taskAssigneeStore.task, updateAssigneeApiCallState.value)
                )
        }
    }

    /**
     * Component displaying a single assignee candidate based on a TaskAssignmentAdvice
     * Lists name and rule scorings for the candidate/task combination along with a button to assign
     */
    inner class AssigneeCandidate(private val advice: TaskAssignmentAdvice) : HTMLComponent() {
        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            val isSelected = advice.person.id == taskAssigneeStore.task.assignee?.personAssignee?.id
            return consumer.button(classes = "card vp-btn-card w-100 text-start shadow-sm p-2 mb-2 rounded") {
                if (isSelected) {
                    classes += "bg-success-subtle"
                }
                disabled = isSelected
                onClickFunction = { event ->
                    selectAssignee(advice.person.copy())
                }

                div (classes = "d-flex flex-wrap mb-2") {
                    h6(classes = "mb-0") {
                        +"${advice.person.anonymizedName} ${advice.personTeam.joinToString(", ") { NevoboTeamName.parse(it.name).teamIdentifier }}"
                        badge(BootstrapColor.Primary) {
                            classes += "ms-2"
                            +advice.person.ageGroup.label
                        }
                        advice.person.groups?.get(GroupType.Scheidsrechters.paramValue)?.forEach { group ->
                            badge(BootstrapColor.Primary) {
                                +group.name
                            }
                        }
                    }

                    if (isSelected) {
                        div (classes = "ms-auto d-flex align-items-center") {
                            badge(BootstrapColor.Success, small = true) { +"Geselecteerd" }
                        }
                    } else {
                        div (classes = "ms-auto vp-btn-card-on-hover align-items-center") {
                            style = "--vp-btn-card-on-hover-display: flex;"
                            badge(BootstrapColor.Primary, small = true) { +"Selecteer" }
                        }
                    }
                }
                div (classes = "row") {
                    div (classes = "col-5") {
                        div(classes = "small text-muted mb-1") {
                            strong { +"${advice.personTaskAmount}" }
                            +" taken / "
                            strong { +"${advice.personMatchAmount}" }
                            +" wedstrijden"
                        }
                        div(classes = "d-flex flex-wrap small text-muted mb-1") {
                            +"vrijwilligers score: "
                            strong { +" ${advice.personVolunteerScore} " }

                            advice.person.volunteerJobs?.forEach { volunteerJob ->
                                badge(color = BootstrapColor.Secondary, small = true) {
                                    classes += "mb-1 me-1"
                                    +volunteerJob.jobName
                                }
                            }
                        }
                    }
                    div (classes = "col-7 border-start border-secondary-subtle") {
                        component(PersonTaskBadges(advice))
                    }
                }
            }
        }

        override suspend fun getState() = assigneeFinderStore.state
            .combine(taskAssigneeStore.state, { a, b -> Pair(a, b) })
            .stateIn(
                CoroutineManager.RENDER_SCOPE,
                SharingStarted.Eagerly,
                Pair(assigneeFinderStore.state.value, taskAssigneeStore.state.value)
            )
    }

    /**
     * Updates the assignee for the task
     * Updates the store on completion so all components can refresh themselves
     */
    fun selectAssignee(person: FullPerson?) {
        launchAsync {
            VolleyPlannerApplication.volleyplannerApiClient
                .organisation
                .plannerConfiguration
                .getTasksEndpoint(problem.slug, problemVersionSlug)
                .updateAssignee(taskAssigneeStore.task, person?.let { PersonId(it.id) })
        }.onComplete {
            taskAssigneeStore.updateTask(
                taskAssigneeStore.task.copy(
                    assignee = taskAssigneeStore.task.assignee.copy(
                        personAssignee = person?.toPublic(),
                        assignedAtTimestamp = LocalDateTime.now()
                    )
                )
            )
            if (problemVersionSlug == null) {
                thuisprogrammaDataManager.problemData.forAllVersions { it.changesBehindPublishedByTaskIdState.refresh() }
            }
        }
    }

    override fun FlowContent.renderBody() {
        logger.debug("renderBody(): ${filterManager.visibleRecordsState.value.size} visible records")
        div(classes = "row-gap-2") {
            val ruleset = assigneeFinderStore.rulesetState.value
            if (ruleset == null) { spinner(small = true) } else {
                val (primaryGroups, backupGroups) = when (taskAssigneeStore.task.name) {
                    "Referee" -> Pair(ruleset.primaryRefereeGroups, ruleset.alternativeRefereeGroups)
                    "ScoreKeeper" -> Pair(ruleset.primaryScoreKeeperGroups, ruleset.alternativeScoreKeeperGroups)
                    else -> Pair(emptyList(), emptyList())
                }
                div(classes = "border-secondary text-bg-light mb-2 p-2") {
                    h6 { +"${match.teamHome} - ${match.teamGuest}" }
                    div (classes = "d-flex") {
                        strong(classes = "me-1") { +TaskLabelConverter.taskNameReadable(taskAssigneeStore.task) }
                        primaryGroups.forEach { groupConstraint ->
                            badge(BootstrapColor.Primary, small = true) {
                                +groupConstraint.groupLabel
                            }
                        }
                        backupGroups.forEach { groupConstraint ->
                            badge(BootstrapColor.Secondary, small = true) { +groupConstraint.groupLabel }
                        }
                    }
                }
            }
            component(CurrentAssigneeSelectionContainer())
            div(classes = "advices-container") {
                if (assigneeFinderStore.state.value == null) {
                    spinner()
                } else {
                    component(AssigneeFilterComponent(assigneeFinderFilters))

                    // TODO: make filtermanager provide a (in)visible context for all records so that we
                    //  can decide here if we want to render with display: none or completely skip
                    filterManager.visibleRecordsState.value
                        .sortedWith(TaskAssignmentAdvice.SCORE_COMPARATOR_DESC)
                        .forEach { advice ->
                            component(AssigneeCandidate(advice))
                        }
                }
            }
        }
    }

    override suspend fun getState(): StateFlow<*> = assigneeFinderStore.state
        .combine(assigneeFinderStore.rulesetState, { a, b -> Pair(a, b) })
        .combine(filterManager.visibleRecordsState, { a, b -> Triple(a.first, a.second, b) })
        .stateIn(
            CoroutineManager.RENDER_SCOPE,
            SharingStarted.Eagerly,
            Triple(assigneeFinderStore.state.value, assigneeFinderStore.rulesetState.value, filterManager.visibleRecordsState.value)
        )
}